import { useEffect, useState, useCallback } from "react";
import { usDate } from "@/lib/dates";
import { viewFromApi } from "@/components/FileViewer";
import { Loader2, CalendarRange, Upload, X, FileText, Trash2, CheckCircle2, AlertCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const today = () => new Date().toISOString().slice(0, 10);

function presetRange(preset) {
  const now = new Date();
  if (preset === "all") return { from: "", to: "" };
  if (preset === "today") return { from: today(), to: today() };
  if (preset === "this_week") {
    const d = new Date(); d.setDate(d.getDate() - d.getDay());
    return { from: d.toISOString().slice(0, 10), to: today() };
  }
  if (preset === "this_month") {
    const d = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: d.toISOString().slice(0, 10), to: today() };
  }
  if (preset === "last_month") {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
  }
  if (preset === "this_year") {
    const d = new Date(now.getFullYear(), 0, 1);
    return { from: d.toISOString().slice(0, 10), to: today() };
  }
  return { from: "", to: "" };
}

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function StudentAttendanceDialog({ student, onClose }) {
  const [range, setRange] = useState({ preset: "this_month", ...presetRange("this_month") });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [excusing, setExcusing] = useState(null); // the date string being excused, or null
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!student) return;
    setLoading(true);
    try {
      const params = {};
      if (range.from) params.from = `${range.from}T00:00:00Z`;
      if (range.to) params.to = `${range.to}T23:59:59Z`;
      const { data } = await api.get(`/students/${student.id}/attendance/summary`, { params });
      setSummary(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load attendance");
    } finally {
      setLoading(false);
    }
  }, [student, range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  const pick = (preset) => setRange({ preset, ...presetRange(preset) });

  const openExcuse = (date, existingNote = "") => { setExcusing(date); setNote(existingNote || ""); setFile(null); };
  const closeExcuse = () => { setExcusing(null); setNote(""); setFile(null); };

  const submitExcuse = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { date: excusing, note: note.trim() };
      if (file) {
        payload.doc_data_url = await fileToDataUrl(file);
        payload.doc_filename = file.name;
      }
      await api.post(`/students/${student.id}/absences`, payload);
      toast.success(`Excuse saved for ${excusing}`);
      closeExcuse();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save excuse");
    } finally {
      setBusy(false);
    }
  };

  const removeExcuse = async (absenceId, date) => {
    if (!confirm(`Remove the excuse for ${date}? This day becomes unexcused.`)) return;
    try {
      await api.delete(`/students/${student.id}/absences/${absenceId}`);
      toast.success("Excuse removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove");
    }
  };

  const viewFile = async (absenceId) => {
    return viewFromApi(api, `/students/${student.id}/absences/${absenceId}/file`, { title: "Excuse document" });
  };

  return (
    <Dialog open={!!student} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="attendance-summary-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-primary" /> {student?.full_name} · Attendance
          </DialogTitle>
          <DialogDescription>
            Excused vs. unexcused absences for the selected range. Upload an excuse note to convert an
            unexcused absence to excused.
          </DialogDescription>
        </DialogHeader>

        {/* Range presets */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {[
            { v: "this_month", label: "This month" },
            { v: "last_month", label: "Last month" },
            { v: "this_year", label: "This year" },
            { v: "this_week", label: "This week" },
            { v: "today", label: "Today" },
            { v: "all", label: "All time" },
          ].map((p) => (
            <button key={p.v} type="button" onClick={() => pick(p.v)} data-testid={`att-preset-${p.v}`}
              className={`h-8 rounded-lg border text-xs font-medium transition ${
                range.preset === p.v ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
              }`}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Summary stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-1" data-testid="attendance-stats">
          <Stat label="School days" value={summary?.school_days} />
          <Stat label="Present" value={summary?.present} tone="green" />
          <Stat label="Absent" value={summary?.absent_total} />
          <Stat label="Excused" value={summary?.absent_excused} tone="green" testid="stat-excused" />
          <Stat label="Unexcused" value={summary?.absent_unexcused} tone="red" testid="stat-unexcused" />
        </div>

        {/* Absence list */}
        <div className="rounded-xl border border-slate-200 overflow-hidden mt-1">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5">Date</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Note / file</th>
                <th className="px-4 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>}
              {!loading && (summary?.absences?.length ?? 0) === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">No absences in this range. 🎉</td></tr>
              )}
              {!loading && summary?.absences?.map((a) => (
                <tr key={a.date} className="border-t border-slate-100" data-testid={`absence-row-${a.date}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-900 whitespace-nowrap">{usDate(a.date)}</td>
                  <td className="px-4 py-2.5">
                    {a.status === "excused" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700" data-testid={`badge-excused-${a.date}`}>
                        <CheckCircle2 className="h-3 w-3" /> Excused
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700" data-testid={`badge-unexcused-${a.date}`}>
                        <AlertCircle className="h-3 w-3" /> Unexcused
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs">
                    {a.note && <div className="line-clamp-2">{a.note}</div>}
                    {a.has_doc && (
                      <button onClick={() => viewFile(a.absence_id)} data-testid={`view-excuse-${a.date}`}
                        className="inline-flex items-center gap-1 text-primary hover:underline mt-0.5">
                        <FileText className="h-3 w-3" /> {a.doc_filename || "View file"}
                      </button>
                    )}
                    {!a.note && !a.has_doc && a.status === "unexcused" && <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {a.status === "unexcused" ? (
                      <button onClick={() => openExcuse(a.date)} data-testid={`excuse-btn-${a.date}`}
                        className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-white hover:shadow transition">
                        <Upload className="h-3 w-3" /> Excuse
                      </button>
                    ) : (
                      <div className="inline-flex gap-2">
                        <button onClick={() => openExcuse(a.date, a.note)} data-testid={`edit-excuse-${a.date}`} className="text-xs text-primary hover:underline">Edit</button>
                        <button onClick={() => removeExcuse(a.absence_id, a.date)} data-testid={`remove-excuse-${a.date}`} className="text-xs text-destructive hover:underline inline-flex items-center gap-0.5">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Inline excuse upload panel */}
        {excusing && (
          <form onSubmit={submitExcuse} className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="excuse-form">
            <div className="flex items-center justify-between">
              <div className="font-display text-sm font-semibold text-slate-900">Excuse for {excusing}</div>
              <button type="button" onClick={closeExcuse} data-testid="excuse-form-close" className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-red-600 text-white shadow-md shadow-red-600/30 hover:bg-red-700 transition-colors h-8 w-8 ml-4"><X className="h-4 w-4" strokeWidth={2.5} /></button>
            </div>
            <label className="block">
              <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Reason (optional)</span>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="excuse-note"
                rows={2} maxLength={2000} placeholder="e.g. Doctor's appointment"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
            </label>
            <label className="block">
              <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Attach note (PDF or image, optional)</span>
              <input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="excuse-file" className="text-sm" />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={closeExcuse} className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100">Cancel</button>
              <button type="submit" disabled={busy} data-testid="excuse-save-btn"
                className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-white inline-flex items-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save excuse
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone, testid }) {
  const toneCls = tone === "green" ? "text-emerald-700" : tone === "red" ? "text-red-700" : "text-slate-900";
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-center" data-testid={testid}>
      <div className={`font-display text-2xl font-bold ${toneCls}`}>{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
